// Vendor Program v2 PR 2 — the borrower "My goals" tab: improvement goals
// (with the monthly grade trail), pending report sign-offs first, and the
// signed-off report history. The report + rating surface is deep-linkable
// via ?report=<id>. Tone: calm and encouraging — the borrower is watching
// their own business improve.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Target } from "lucide-react";
import { Button, EmptyState } from "@/design-system/components/ui";
import {
  formatReportMonth,
  formatReportMonthShort,
  portalBorrowerApi,
  type BorrowerGoalDto,
  type BorrowerVendorReportDto,
} from "../api/borrower";
import { BorrowerGoalCard } from "../components/BorrowerGoalCard";
import { BorrowerReportSignOff } from "../components/BorrowerReportSignOff";
import { BorrowerStarsDisplay } from "../components/BorrowerStarRating";

export function BorrowerGoalsTab() {
  const [goals, setGoals] = useState<BorrowerGoalDto[] | null>(null);
  const [reports, setReports] = useState<BorrowerVendorReportDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [g, r] = await Promise.all([
          portalBorrowerApi.listGoals(),
          portalBorrowerApi.listVendorReports(),
        ]);
        if (!cancelled) {
          setGoals(g);
          setReports(r);
        }
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "Couldn't load your goals.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenReportId = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("report", id);
    else updated.delete("report");
    setParams(updated, { replace: true });
  };

  // The sign-off surface fetches its own report, so a ?report= deep link
  // works even before (or if) the lists load.
  if (openReportId) {
    return (
      <BorrowerReportSignOff
        reportId={openReportId}
        onBack={() => setOpenReportId(null)}
        onSignedOff={(updated) =>
          setReports(
            (prev) =>
              prev?.map((r) => (r.reportId === updated.reportId ? updated : r)) ??
              prev,
          )
        }
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!goals || !reports) {
    return (
      <p className="text-small text-gray-mid" data-testid="goals-loading">
        Loading your goals…
      </p>
    );
  }

  if (goals.length === 0 && reports.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="No goals yet"
        description="When your improvement goals are set with Phoenix, they'll appear here — along with monthly progress from the vendor working with you."
      />
    );
  }

  const pending = reports.filter((r) => !r.signedOff);
  const history = reports.filter((r) => r.signedOff);
  const active = goals.filter((g) => g.status === "active");
  const completed = goals.filter((g) => g.status !== "active");
  const multiLoan = new Set(goals.map((g) => g.loanId)).size > 1;

  return (
    <section aria-label="My goals" className="flex flex-col gap-space-5">
      <p className="text-small text-gray-mid m-0">
        Your improvement goals, set with Phoenix from your loan&apos;s
        underwriting — here&apos;s how they&apos;re progressing.
      </p>

      {pending.length > 0 && (
        <div className="flex flex-col gap-space-3" data-testid="pending-sign-offs">
          {pending.map((report) => (
            <div
              key={report.reportId}
              className="rounded-lg border border-gold bg-white-warm p-space-4 flex items-center justify-between gap-space-3 flex-wrap"
              data-testid={`pending-report-${report.reportId}`}
            >
              <div>
                <p className="text-small font-medium text-onyx m-0">
                  {report.vendorName}&apos;s {formatReportMonth(report.periodStart)}{" "}
                  report is ready for your review
                </p>
                <p className="text-caption text-gray-mid m-0 mt-space-1">
                  {report.loanLabel} · a quick read and three star ratings —
                  under a minute.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setOpenReportId(report.reportId)}
                data-testid={`review-report-${report.reportId}`}
              >
                Review &amp; sign off
              </Button>
            </div>
          ))}
        </div>
      )}

      {active.length > 0 && (
        <div className="flex flex-col gap-space-3" data-testid="active-goals">
          {multiLoan ? (
            groupByLoan(active).map(([loanLabel, loanGoals]) => (
              <div key={loanLabel} className="flex flex-col gap-space-3">
                <h3 className="font-display font-semibold text-h4 m-0">
                  {loanLabel}
                </h3>
                {loanGoals.map((goal) => (
                  <BorrowerGoalCard key={goal.id} goal={goal} />
                ))}
              </div>
            ))
          ) : (
            active.map((goal) => <BorrowerGoalCard key={goal.id} goal={goal} />)
          )}
        </div>
      )}

      {completed.length > 0 && (
        <div className="flex flex-col gap-space-3" data-testid="completed-goals">
          <h3 className="font-display font-semibold text-h4 m-0 text-gray-mid">
            Completed goals
          </h3>
          {completed.map((goal) => (
            <BorrowerGoalCard key={goal.id} goal={goal} muted />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="flex flex-col gap-space-2" data-testid="report-history">
          <h3 className="font-display font-semibold text-h4 m-0">
            Past reports
          </h3>
          <ul className="m-0 p-0 list-none flex flex-col">
            {history.map((report) => (
              <li key={report.reportId}>
                <button
                  type="button"
                  onClick={() => setOpenReportId(report.reportId)}
                  className="w-full flex items-center justify-between gap-space-3 flex-wrap text-left py-space-2 border-b border-border-light hover:bg-white-warm rounded-sm px-space-2"
                  data-testid={`history-report-${report.reportId}`}
                >
                  <span className="text-small text-onyx">
                    {formatReportMonthShort(report.periodStart)} ·{" "}
                    {report.vendorName}
                  </span>
                  {report.signOff && (
                    <BorrowerStarsDisplay
                      value={report.signOff.overallRating}
                      label={`Your rating: ${report.signOff.overallRating} of 4 stars`}
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function groupByLoan(
  goals: BorrowerGoalDto[],
): [string, BorrowerGoalDto[]][] {
  const groups = new Map<string, BorrowerGoalDto[]>();
  for (const goal of goals) {
    const existing = groups.get(goal.loanLabel);
    if (existing) existing.push(goal);
    else groups.set(goal.loanLabel, [goal]);
  }
  return Array.from(groups.entries());
}
